/**
 * Describe Your Trip AI search — structured `/nlsearch` interpretation.
 */
import CONFIG from 'isomorphic-config'
import fetch from 'isomorphic-unfetch'

import { jsonContentTypeHeader } from '../../../server/constants'
import { logAiSearchRequestFailed, logAiSearchRequestTimeout } from './logAiSearch'

export const NLSEARCH_TIMEOUT_MS = 4000

type FetchNlSearchParams = {
  query: string
  timezone: string
  signal?: AbortSignal
}

export class AiSearchTimeoutError extends Error {
  constructor() {
    super('AI_SEARCH_TIMEOUT')
    this.name = 'TimeoutError'
  }
}

export type NlSearchFilter = {
  filterType: string
  filterValue: string
  filterLabel: string
}

export type NlSearchDestination = {
  type?: string
  id?: string
  cityId?: string
  displayLine1?: string | null
  displayLine2?: string | null
}

export type NlSearchOccupancy = {
  rooms?: number
  adults?: number
  children?: number
}

export type NlSearchResponse = {
  originalQuery?: string | null
  destination?: NlSearchDestination | null
  checkIn?: string | null
  checkOut?: string | null
  occupancy?: NlSearchOccupancy | null
  minPrice?: number | null
  maxPrice?: number | null
  filters?: NlSearchFilter[] | null
  routeToPenny?: boolean
}

function isAbortError(error: unknown) {
  return typeof error === 'object' && error !== null && 'name' in error && error.name === 'AbortError'
}

export function getBrowserTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  } catch {
    return 'UTC'
  }
}

export function getNlSearchUrl() {
  return CONFIG.client.typeahead.hotelsNlSearch
}

function isNlSearchResponse(value: unknown): value is NlSearchResponse {
  return typeof value === 'object' && value !== null
}

/**
 * Fetches a structured hotel interpretation from `hotels/nlsearch`.
 * Aborts after `NLSEARCH_TIMEOUT_MS` unless the caller aborts first.
 */
export async function fetchNlSearch({
  query,
  timezone,
  signal,
}: FetchNlSearchParams): Promise<NlSearchResponse> {
  const timeoutController = new AbortController()
  const timeoutId = setTimeout(() => {
    timeoutController.abort()
  }, NLSEARCH_TIMEOUT_MS)

  const onCallerAbort = () => {
    timeoutController.abort()
  }
  signal?.addEventListener('abort', onCallerAbort)

  try {
    const response = await fetch(getNlSearchUrl(), {
      method: 'POST',
      headers: jsonContentTypeHeader,
      body: JSON.stringify({ query, timezone }),
      signal: timeoutController.signal,
    })

    if (!response.ok) {
      throw new Error(`NLSEARCH_HTTP_${response.status}`)
    }

    const payload: unknown = await response.json()
    if (!isNlSearchResponse(payload)) {
      throw new Error('NLSEARCH_INVALID_BODY')
    }

    return payload
  } catch (error) {
    const callerAborted = Boolean(signal?.aborted)
    if (callerAborted) throw error

    if (timeoutController.signal.aborted) {
      logAiSearchRequestTimeout(query)
      throw new AiSearchTimeoutError()
    }

    if (!isAbortError(error)) logAiSearchRequestFailed(query)
    throw error
  } finally {
    clearTimeout(timeoutId)
    signal?.removeEventListener('abort', onCallerAbort)
  }
}
